import React from 'react';

export type BillingForm = {
  company: string;
  manager: string;
  post1: string;
  post2: string;
  pref: string;
  city: string;
  address1: string;
  address2: string;
  tel: string;
  logo: File | null;
};

export type BillingSectionProps = {
  billingForm: BillingForm;
  setBillingForm: React.Dispatch<React.SetStateAction<BillingForm>>;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  PREFS: string[];
  logoInputRef: React.RefObject<HTMLInputElement>;
};

const BillingSection: React.FC<BillingSectionProps> = ({
  billingForm,
  setBillingForm,
  open,
  setOpen,
  PREFS,
  logoInputRef,
}) => {
  const handleBillingChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'file') {
      const input = e.target as HTMLInputElement;
      setBillingForm((prev) => ({ ...prev, [name]: input.files && input.files[0] ? input.files[0] : null }));
    } else {
      setBillingForm((prev) => ({ ...prev, [name]: value }));
    }
  };
  const handleBillingLogoClear = () => {
    setBillingForm((prev) => ({ ...prev, logo: null }));
    if (logoInputRef.current) {
      logoInputRef.current.value = '';
    }
  };

  return (
    <div>
      <button type="button" className={`flex items-center justify-between w-full pt-1 pb-2 text-left text-lg font-bold text-gray-800 border-b border-gray-200 focus:outline-none`} onClick={() => setOpen((v) => !v)}>
        <span>請求情報</span>
        <span className="text-base">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="p-4 text-sm text-gray-600 bg-white border border-gray-200 rounded-b-lg shadow-md">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block mb-1 text-sm font-semibold text-gray-500">請求先会社名</label>
              <input type="text" name="company" className="w-full px-2 py-1 border border-gray-200 rounded" value={billingForm.company} onChange={handleBillingChange} />
            </div>
            <div>
              <label className="block mb-1 text-sm font-semibold text-gray-500">担当者名</label>
              <input type="text" name="manager" className="w-full px-2 py-1 border border-gray-200 rounded" value={billingForm.manager} onChange={handleBillingChange} />
            </div>
            <div className="flex gap-2">
              <div className="w-1/2">
                <label className="block mb-1 text-sm font-semibold text-gray-500">郵便番号（上3桁）</label>
                <input type="text" name="post1" className="w-full px-2 py-1 border border-gray-200 rounded" value={billingForm.post1} onChange={handleBillingChange} maxLength={3} />
              </div>
              <div className="w-1/2">
                <label className="block mb-1 text-sm font-semibold text-gray-500">郵便番号（下4桁）</label>
                <input type="text" name="post2" className="w-full px-2 py-1 border border-gray-200 rounded" value={billingForm.post2} onChange={handleBillingChange} maxLength={4} />
              </div>
            </div>
            <div>
              <label className="block mb-1 text-sm font-semibold text-gray-500">都道府県</label>
              <select name="pref" className="w-full px-2 py-1 text-sm font-semibold text-gray-500 border border-gray-200 rounded" value={billingForm.pref} onChange={handleBillingChange}>
                <option value="">選択してください</option>
                {PREFS.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block mb-1 text-sm font-semibold text-gray-500">市区町村</label>
              <input type="text" name="city" className="w-full px-2 py-1 border border-gray-200 rounded" value={billingForm.city} onChange={handleBillingChange} />
            </div>
            <div>
              <label className="block mb-1 text-sm font-semibold text-gray-500">住所１</label>
              <input type="text" name="address1" className="w-full px-2 py-1 border border-gray-200 rounded" value={billingForm.address1} onChange={handleBillingChange} />
            </div>
            <div>
              <label className="block mb-1 text-sm font-semibold text-gray-500">住所２</label>
              <input type="text" name="address2" className="w-full px-2 py-1 border border-gray-200 rounded" value={billingForm.address2} onChange={handleBillingChange} />
            </div>
            <div>
              <label className="block mb-1 text-sm font-semibold text-gray-500">電話番号</label>
              <input type="text" name="tel" className="w-full px-2 py-1 border border-gray-200 rounded" value={billingForm.tel} onChange={handleBillingChange} />
            </div>
            {/* 会社ロゴ */}
            <div className="md:col-span-2">
              <label className="block mb-1 text-sm font-semibold text-gray-500">会社ロゴ</label>
              <input
                type="file"
                name="logo"
                accept="image/*"
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
                onChange={handleBillingChange}
                ref={logoInputRef}
              />
              {billingForm.logo && (
                <div className="relative flex items-center gap-4 mt-2">
                  <div className="relative inline-block">
                    <img src={URL.createObjectURL(billingForm.logo)} alt="会社ロゴプレビュー" className="object-contain h-16 border rounded" />
                    <button
                      type="button"
                      className="absolute -top-2 -right-2 bg-white border border-gray-300 rounded-full p-0.5 shadow hover:bg-gray-100"
                      style={{ lineHeight: 1 }}
                      onClick={handleBillingLogoClear}
                      aria-label="画像をキャンセル"
                    >
                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="9" cy="9" r="9" fill="#fff"/>
                        <path d="M6 6l6 6M12 6l-6 6" stroke="#888" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingSection;
